/**
 * DayOnePage — Cinematic Day 1 orientation for The Operator Method.
 *
 * Routes here after Stripe checkout (?payment=success) or anytime a paid
 * student visits /day-one.
 *
 * Architecture:
 *   1. Payment polling (absorbs Stripe webhook delay)
 *   2. Cinematic fade-in, then continuous scroll-reveal of full narration
 *   3. TTS pre-loads on mount; loading state shows animated pulse + ring
 *   4. Once audio is ready: auto-plays the FULL narration top-to-bottom
 *   5. Text scrolls up smoothly (Star Wars style, no perspective) in sync
 *   6. "Begin Training →" CTA appears after narration completes
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase-config';
import { useAuth } from '../context/AuthContext';
import NeuralNexusBackground from '../components/shared/NeuralNexusBackground';
import { DESIGN_SYSTEM } from '../theme/designSystem';

const openaiTTS = httpsCallable(functions, 'openaiTTS');

// ─── Constants ──────────────────────────────────────────────────────────────

const TIER_META = {
  foundations: { label: 'Foundations',        color: '#4f8ef7', modules: 'Modules 0 – 1 (9 lessons)', weeks: '2–3 weeks' },
  operator:   { label: 'Full Operator',      color: '#7c3aed', modules: 'All 5 modules (25 lessons)', weeks: '3–4 weeks' },
  certified:  { label: 'Certified Operator',  color: '#f59e0b', modules: 'All 5 modules + certification exam', weeks: '4–5 weeks' },
};

// The full narration — read as one continuous piece by TTS
function buildNarration(tierInfo) {
  return [
    `Welcome to The Operator Method. I'm your instructor, and I just want to say — I'm really glad you're here.`,
    `I know starting something new can feel uncertain. Maybe you're wondering if this will actually work, or if you'll keep up, or whether AI training is even the right move right now. Those feelings are completely normal, and I want you to hear this: you're in the right place.`,
    `This course was built for people exactly like you — professionals who already use AI but know they're leaving value on the table. Over the next few weeks, I'm going to show you exactly how to close that gap. Not with theory or buzzwords — with a system you can use starting day one.`,
    `Before we begin, let me walk you through how everything works so there are no surprises.`,
    `How Training Sessions Work.`,
    `Each session is a live conversation between you and your instructor. I'll assess where you are, teach specific skills, and guide you through hands-on exercises. You'll work through structured lessons, each one building on the last. Sessions are self-paced. Take your time, step away, come back anytime. Your progress saves automatically after every exchange.`,
    `How to Interact With Your Instructor.`,
    `Talk to me like you would a real instructor — ask questions, push back, request examples. If something doesn't make sense, say so. I'll rephrase until it clicks. You can type naturally. No special commands or syntax required. I'll tell you when to try something hands-on in the sandbox — just follow my lead. If I ask you a question, give me a real answer. The more honest you are, the better I can teach you.`,
    `The Interactive Sandbox.`,
    `During sessions, I'll open an interactive sandbox where you practice directing AI in real time. The sandbox appears automatically when I cue it — you'll never have to find it yourself. When I say "let's try it" or "open the sandbox," it slides into view. When the exercise is done, the sandbox closes and we continue our conversation. Think of it as your training floor — a safe space to experiment while I watch and coach.`,
    `Course Timeline and Pacing.`,
    `Your ${tierInfo.label} track includes ${tierInfo.modules}. Recommended pace: ${tierInfo.weeks}, but there's no hard deadline. Each lesson takes roughly 30 to 45 minutes, depending on how deep you want to go. This is fully self-paced. Work when it fits your schedule. Your progression is saved automatically — no work is ever lost.`,
    `What I Expect From You.`,
    `Treat our sessions like a real training environment — professional and focused. Give honest, thoughtful answers. The course adapts to you — surface-level responses produce surface-level results. One account per person. Account sharing is prohibited. Abusive or disrespectful language will end your session immediately.`,
    `What You Can Expect From Me.`,
    `Honest, direct feedback on your work — no empty praise. Patient explanation. If something doesn't land, I'll try again a different way. Structured progression that builds real, transferable skill. I won't move you forward until you've actually understood the material.`,
    `Logging Out and Transcripts.`,
    `Use the Sign Out button in the top navigation bar when you're done. Your progress saves automatically — you'll pick up exactly where you left off. After each training session, you'll have the option to purchase a full session transcript as a PDF. Transcripts include everything: lessons, your responses, exercises, and instructor feedback. Build a personal library of your learning journey to reference anytime.`,
    `That's everything. No more setup, no more forms. When you're ready, click Begin Training and we'll start your first real session together.`,
  ].join('\n\n');
}

// The narration broken into visual segments for the scroll reveal
function buildSegments(tierInfo) {
  return [
    { type: 'welcome', text: `Welcome to The Operator Method. I'm your instructor, and I just want to say — I'm really glad you're here.\n\nI know starting something new can feel uncertain. Maybe you're wondering if this will actually work, or if you'll keep up, or whether AI training is even the right move right now. Those feelings are completely normal, and I want you to hear this: you're in the right place.\n\nThis course was built for people exactly like you — professionals who already use AI but know they're leaving value on the table. Over the next few weeks, I'm going to show you exactly how to close that gap. Not with theory or buzzwords — with a system you can use starting day one.\n\nBefore we begin, let me walk you through how everything works so there are no surprises.` },
    { type: 'heading', text: 'How Training Sessions Work' },
    { type: 'body', text: `Each session is a live conversation between you and your instructor. I'll assess where you are, teach specific skills, and guide you through hands-on exercises. You'll work through structured lessons, each one building on the last. Sessions are self-paced. Take your time, step away, come back anytime. Your progress saves automatically after every exchange.` },
    { type: 'heading', text: 'How to Interact With Your Instructor' },
    { type: 'body', text: `Talk to me like you would a real instructor — ask questions, push back, request examples. If something doesn't make sense, say so. I'll rephrase until it clicks. You can type naturally. No special commands or syntax required. I'll tell you when to try something hands-on in the sandbox — just follow my lead. If I ask you a question, give me a real answer. The more honest you are, the better I can teach you.` },
    { type: 'heading', text: 'The Interactive Sandbox' },
    { type: 'body', text: `During sessions, I'll open an interactive sandbox where you practice directing AI in real time. The sandbox appears automatically when I cue it — you'll never have to find it yourself. When I say "let's try it" or "open the sandbox," it slides into view. When the exercise is done, the sandbox closes and we continue our conversation. Think of it as your training floor — a safe space to experiment while I watch and coach.` },
    { type: 'heading', text: 'Course Timeline & Pacing' },
    { type: 'body', text: `Your ${tierInfo.label} track includes ${tierInfo.modules}. Recommended pace: ${tierInfo.weeks}, but there's no hard deadline. Each lesson takes roughly 30–45 minutes, depending on how deep you want to go. This is fully self-paced. Work when it fits your schedule. Your progression is saved automatically — no work is ever lost.` },
    { type: 'heading', text: 'What I Expect From You' },
    { type: 'body', text: `Treat our sessions like a real training environment — professional and focused. Give honest, thoughtful answers. The course adapts to you — surface-level responses produce surface-level results. One account per person. Account sharing is prohibited. Abusive or disrespectful language will end your session immediately.` },
    { type: 'heading', text: 'What You Can Expect From Me' },
    { type: 'body', text: `Honest, direct feedback on your work — no empty praise. Patient explanation. If something doesn't land, I'll try again a different way. Structured progression that builds real, transferable skill. I won't move you forward until you've actually understood the material.` },
    { type: 'heading', text: 'Logging Out & Transcripts' },
    { type: 'body', text: `Use the Sign Out button in the top navigation bar when you're done. Your progress saves automatically — you'll pick up exactly where you left off. After each training session, you'll have the option to purchase a full session transcript as a PDF ($10). Transcripts include everything: lessons, your responses, exercises, and instructor feedback. Build a personal library of your learning journey to reference anytime.` },
    { type: 'cta', text: `That's everything. No more setup, no more forms. When you're ready, click below and we'll start your first real training session together.` },
  ];
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function DayOnePage() {
  const { currentUser, userProfile, loading: authLoading, isAdmin } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const isPaymentReturn = new URLSearchParams(location.search).has('payment');

  // ── Auth / tier state ─────────────────────────────────────────────────────
  const [tierReady, setTierReady]       = useState(false);
  const [pollLabel, setPollLabel]       = useState('Activating your access…');
  const [pollFailed, setPollFailed]     = useState(false);

  // ── Cinematic state ───────────────────────────────────────────────────────
  const [phase, setPhase]         = useState('loading');  // loading | intro | scrolling | done
  const [audioReady, setAudioReady] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [revealIndex, setRevealIndex] = useState(-1);     // which segment is visible (-1 = none)
  const playerRef = useRef(null);
  const scrollRef = useRef(null);
  const revealTimerRef = useRef(null);

  // ── Payment polling ───────────────────────────────────────────────────────
  useEffect(() => {
    if (authLoading) return;
    const hasTier = isAdmin || !!userProfile?.tier;
    if (hasTier) { setTierReady(true); return; }
    if (!isPaymentReturn) { navigate('/pricing', { replace: true }); return; }
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 12; i++) {
        await new Promise(r => setTimeout(r, 1500));
        if (cancelled) return;
        try {
          const snap = await getDoc(doc(db, 'users', currentUser.uid));
          if (snap.exists() && snap.data()?.tier) {
            setTierReady(true);
            window.history.replaceState({}, '', '/day-one');
            return;
          }
        } catch { /* retry */ }
        if (i === 5) setPollLabel('Almost there — confirming your payment…');
      }
      if (!cancelled) setPollFailed(true);
    })();
    return () => { cancelled = true; };
  }, [authLoading, currentUser, userProfile, isAdmin, isPaymentReturn, navigate]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const tier = isAdmin ? 'certified' : (userProfile?.tier || 'foundations');
  const tierInfo = TIER_META[tier] || TIER_META.foundations;
  const segments = buildSegments(tierInfo);
  const narrationText = buildNarration(tierInfo);

  // ── Pre-load TTS on mount (or pull from pre-cached sessionStorage) ────────
  useEffect(() => {
    if (!tierReady) return;
    let cancelled = false;

    // Check for pre-loaded audio from Dashboard
    const cached = sessionStorage.getItem('om_dayone_tts');
    if (cached) {
      sessionStorage.removeItem('om_dayone_tts');
      try {
        const bytes = Uint8Array.from(atob(cached), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        if (!cancelled) { setAudioUrl(url); setAudioReady(true); }
        return;
      } catch { /* fall through to fresh fetch */ }
    }

    const voice = (() => { try { return localStorage.getItem('om_tts_voice') || 'nova'; } catch { return 'nova'; } })();
    const speed = (() => { try { return parseFloat(localStorage.getItem('om_tts_rate')) || 0.9; } catch { return 0.9; } })();

    (async () => {
      try {
        const result = await openaiTTS({ text: narrationText, voice, speed });
        if (cancelled) return;
        const b64 = result?.data?.audio;
        if (!b64) { setAudioReady(true); return; } // proceed without audio
        const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
        const blob  = new Blob([bytes], { type: result.data.mimeType || 'audio/mpeg' });
        const url   = URL.createObjectURL(blob);
        setAudioUrl(url);
        setAudioReady(true);
      } catch {
        if (!cancelled) setAudioReady(true); // proceed silently
      }
    })();

    return () => { cancelled = true; };
  }, [tierReady, narrationText]);

  // ── Cinematic intro sequence ──────────────────────────────────────────────
  useEffect(() => {
    if (!tierReady || !audioReady) return;
    // Quick fade-in intro then start scrolling
    setPhase('intro');
    const t = setTimeout(() => {
      setPhase('scrolling');
    }, 1800);
    return () => clearTimeout(t);
  }, [tierReady, audioReady]);

  // ── Start audio + sequential reveal when scrolling begins ─────────────────
  useEffect(() => {
    if (phase !== 'scrolling') return;

    const audioEnabled = (() => { try { return localStorage.getItem('om_audio_enabled') !== 'off'; } catch { return true; } })();

    // Start audio playback
    if (audioUrl && audioEnabled) {
      const player = new Audio(audioUrl);
      playerRef.current = player;
      player.play().catch(() => {});
      player.onended = () => { playerRef.current = null; };
    }

    // Sequential reveal: each segment fades in on a timed interval
    // Total narration is ~2.5 min at 0.9x speed. Distribute segments evenly.
    const totalSegments = segments.length;
    // Estimate: ~10s per segment for audio pacing
    const msPerSegment = audioUrl && audioEnabled ? 10000 : 2200;

    let idx = 0;
    setRevealIndex(0);
    revealTimerRef.current = setInterval(() => {
      idx += 1;
      if (idx >= totalSegments) {
        clearInterval(revealTimerRef.current);
        setPhase('done');
        return;
      }
      setRevealIndex(idx);
    }, msPerSegment);

    return () => {
      clearInterval(revealTimerRef.current);
      if (playerRef.current) { playerRef.current.pause(); playerRef.current = null; }
    };
  }, [phase, audioUrl, segments.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-scroll to newly revealed segment ─────────────────────────────────
  useEffect(() => {
    if (revealIndex < 0 || !scrollRef.current) return;
    const el = scrollRef.current.children[revealIndex];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [revealIndex]);

  // ── Cleanup ───────────────────────────────────────────────────────────────
  useEffect(() => () => {
    if (playerRef.current) { playerRef.current.pause(); playerRef.current = null; }
    clearInterval(revealTimerRef.current);
  }, []);

  // ── Begin Training handler ────────────────────────────────────────────────
  async function handleBeginTraining() {
    if (playerRef.current) { playerRef.current.pause(); playerRef.current = null; }
    if (currentUser) {
      try {
        await setDoc(doc(db, 'users', currentUser.uid), {
          dayOneCompleted: true,
          dayOneCompletedAt: serverTimestamp(),
        }, { merge: true });
      } catch { /* non-fatal */ }
    }
    navigate('/housekeeping-info', { replace: true });
  }

  // ── Skip to end (reveal all, stop audio) ──────────────────────────────────
  function handleSkip() {
    clearInterval(revealTimerRef.current);
    if (playerRef.current) { playerRef.current.pause(); playerRef.current = null; }
    setRevealIndex(segments.length - 1);
    setPhase('done');
  }

  // ── Manual re-check for stuck polling ─────────────────────────────────────
  async function handleRecheck() {
    setPollFailed(false);
    setPollLabel('Checking again…');
    try {
      const snap = await getDoc(doc(db, 'users', currentUser.uid));
      if (snap.exists() && snap.data()?.tier) {
        setTierReady(true);
        window.history.replaceState({}, '', '/day-one');
        return;
      }
    } catch { /* ignore */ }
    setPollFailed(true);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  // Payment polling
  if (authLoading || (!tierReady && isPaymentReturn)) {
    return (
      <div style={s.page}>
        <NeuralNexusBackground />
        <div style={s.centerBox}>
          <div style={s.pulseRing} />
          <p style={s.pollText}>{pollLabel}</p>
          {pollFailed && (
            <>
              <p style={s.pollSub}>Payment received — access is taking longer than expected.</p>
              <button onClick={handleRecheck} style={s.recheckBtn}>Check Again</button>
              <button onClick={() => navigate('/housekeeping-info')} style={s.skipBtn}>Continue to Course →</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Loading TTS (animated waiting state)
  if (phase === 'loading') {
    return (
      <div style={s.page}>
        <NeuralNexusBackground />
        <div style={s.centerBox}>
          <div style={s.loadingRing}>
            <div style={s.loadingRingInner} />
          </div>
          <p style={s.loadingLabel}>Preparing your orientation…</p>
          <div style={s.loadingSubLabel}>
            <span style={s.tierPillSmall}>
              <span style={{ color: tierInfo.color, fontWeight: 700 }}>{tierInfo.label}</span>
              <span style={{ color: '#3a5070', margin: '0 6px' }}>·</span>
              <span>Enrolled</span>
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Cinematic intro fade
  if (phase === 'intro') {
    return (
      <div style={s.page}>
        <NeuralNexusBackground />
        <div style={{ ...s.centerBox, animation: 'dayOneFadeIn 1.6s ease forwards' }}>
          <h1 style={{ ...s.introTitle, animation: 'dayOneSlideUp 1.4s ease forwards' }}>
            Welcome to Day One
          </h1>
          <div style={{ ...s.tierPillSmall, animation: 'dayOneFadeIn 1.8s ease forwards', animationDelay: '0.3s', opacity: 0, animationFillMode: 'forwards' }}>
            <span style={{ color: tierInfo.color, fontWeight: 700 }}>{tierInfo.label}</span>
          </div>
        </div>
        <style>{cinematicKeyframes}</style>
      </div>
    );
  }

  // Main scrolling narration
  return (
    <div style={s.page}>
      <NeuralNexusBackground />
      <style>{cinematicKeyframes}</style>

      {/* Skip button (top-right, subtle) */}
      {phase === 'scrolling' && (
        <button onClick={handleSkip} style={s.skipNarrationBtn}>Skip ▸</button>
      )}

      <div ref={scrollRef} style={s.scrollContainer}>
        {segments.map((seg, i) => {
          const visible = i <= revealIndex;
          const baseDelay = `${Math.min(i * 0.05, 0.3)}s`;

          if (seg.type === 'welcome') {
            return (
              <div key={i} style={{
                ...s.segWelcome,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(30px)',
                transition: `opacity 1s ease ${baseDelay}, transform 1s ease ${baseDelay}`,
              }}>
                <p style={s.segWelcomeText}>{seg.text}</p>
              </div>
            );
          }

          if (seg.type === 'heading') {
            return (
              <div key={i} style={{
                ...s.segHeading,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.8s ease ${baseDelay}, transform 0.8s ease ${baseDelay}`,
              }}>
                <span style={s.segHeadingIcon}>◈</span>
                <h2 style={s.segHeadingText}>{seg.text}</h2>
              </div>
            );
          }

          if (seg.type === 'body') {
            return (
              <div key={i} style={{
                ...s.segBody,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.8s ease ${baseDelay}, transform 0.8s ease ${baseDelay}`,
              }}>
                <p style={s.segBodyText}>{seg.text}</p>
              </div>
            );
          }

          if (seg.type === 'cta') {
            return (
              <div key={i} style={{
                ...s.segCta,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.97)',
                transition: `opacity 1s ease ${baseDelay}, transform 1s ease ${baseDelay}`,
              }}>
                <p style={s.segCtaText}>{seg.text}</p>
                <button onClick={handleBeginTraining} style={s.ctaBtn}>
                  Begin Training →
                </button>
                <p style={s.ctaSub}>You can revisit this page from your dashboard anytime.</p>
              </div>
            );
          }

          return null;
        })}

        {/* Extra bottom padding so last items can scroll into center */}
        <div style={{ minHeight: '40vh' }} />
      </div>
    </div>
  );
}

// ─── Keyframe animations injected via <style> ───────────────────────────────

const cinematicKeyframes = `
@keyframes dayOneFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes dayOneSlideUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes dayOnePulse {
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50%      { transform: scale(1.15); opacity: 1; }
}
@keyframes dayOneRingSpin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
`;

// ─── Styles ─────────────────────────────────────────────────────────────────

const s = {
  page: {
    position: 'relative',
    minHeight: '100vh',
    background: DESIGN_SYSTEM.backgrounds.course,
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#dce8ff',
    overflow: 'hidden',
    paddingTop: `${DESIGN_SYSTEM.layout.globalNavHeight}px`,
    boxSizing: 'border-box',
  },

  // ── Shared center layout ──────────────────────────────────────────────
  centerBox: {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    minHeight: `calc(100vh - ${DESIGN_SYSTEM.layout.globalNavHeight}px)`,
  },

  // ── Polling ───────────────────────────────────────────────────────────
  pulseRing: {
    width: '52px', height: '52px', borderRadius: '50%',
    border: '3px solid rgba(0,212,255,0.4)',
    animation: 'dayOnePulse 1.5s ease-in-out infinite',
  },
  pollText: { fontSize: '18px', fontWeight: 600, color: '#b0c8e8', margin: 0 },
  pollSub:  { fontSize: '14px', color: '#5a7a9a', margin: 0, maxWidth: '380px' },
  recheckBtn: {
    padding: '12px 28px', backgroundColor: '#00d4ff', color: '#001833',
    border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', marginTop: '8px',
  },
  skipBtn: {
    padding: '10px 20px', backgroundColor: 'transparent', color: '#7a9fc0',
    border: '1px solid rgba(153,179,214,0.2)', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
  },

  // ── Loading (TTS pre-load) ────────────────────────────────────────────
  loadingRing: {
    width: '64px', height: '64px', borderRadius: '50%',
    border: '3px solid rgba(57,213,255,0.12)',
    borderTopColor: 'rgba(57,213,255,0.6)',
    animation: 'dayOneRingSpin 1.2s linear infinite',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  loadingRingInner: {
    width: '8px', height: '8px', borderRadius: '50%',
    background: 'rgba(57,213,255,0.5)',
    animation: 'dayOnePulse 1.8s ease-in-out infinite',
  },
  loadingLabel: { fontSize: '16px', fontWeight: 600, color: '#7a9fc0', margin: 0 },
  loadingSubLabel: { marginTop: '4px' },

  // ── Tier pill ─────────────────────────────────────────────────────────
  tierPillSmall: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '5px 14px', borderRadius: '999px',
    border: '1px solid rgba(79,142,247,0.25)', background: 'rgba(8,18,44,0.6)',
    fontSize: '11px', fontWeight: 600, color: '#7a9fc0',
  },

  // ── Intro ─────────────────────────────────────────────────────────────
  introTitle: {
    fontSize: '36px', fontWeight: 800, color: '#f0f8ff',
    margin: 0, letterSpacing: '-0.02em',
  },

  // ── Scroll container ──────────────────────────────────────────────────
  scrollContainer: {
    position: 'relative', zIndex: 1,
    maxWidth: '660px', margin: '0 auto',
    padding: '60px 24px 0',
    display: 'flex', flexDirection: 'column', gap: '0',
    overflowY: 'auto',
    height: `calc(100vh - ${DESIGN_SYSTEM.layout.globalNavHeight}px)`,
  },

  // ── Skip narration button ─────────────────────────────────────────────
  skipNarrationBtn: {
    position: 'fixed', top: `${DESIGN_SYSTEM.layout.globalNavHeight + 14}px`, right: '20px',
    zIndex: 10, padding: '6px 14px', borderRadius: '999px',
    border: '1px solid rgba(153,179,214,0.2)', background: 'rgba(10,14,26,0.8)',
    color: '#5a7a9a', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
    backdropFilter: 'blur(8px)',
  },

  // ── Segment: Welcome ──────────────────────────────────────────────────
  segWelcome: {
    textAlign: 'center', paddingBottom: '40px', marginBottom: '8px',
  },
  segWelcomeText: {
    fontSize: '15px', lineHeight: 1.85, color: '#b0c8e8',
    margin: '0 auto', maxWidth: '580px', textAlign: 'left', whiteSpace: 'pre-line',
  },

  // ── Segment: Heading ──────────────────────────────────────────────────
  segHeading: {
    display: 'flex', alignItems: 'center', gap: '10px',
    paddingTop: '28px', paddingBottom: '8px',
    borderTop: '1px solid rgba(153,179,214,0.08)',
  },
  segHeadingIcon: {
    fontSize: '14px', color: '#39d5ff', flexShrink: 0,
  },
  segHeadingText: {
    fontSize: '17px', fontWeight: 700, color: '#f0f8ff', margin: 0,
  },

  // ── Segment: Body ─────────────────────────────────────────────────────
  segBody: {
    paddingBottom: '20px',
  },
  segBodyText: {
    fontSize: '14px', lineHeight: 1.8, color: '#9ab0cc', margin: 0,
  },

  // ── Segment: CTA ──────────────────────────────────────────────────────
  segCta: {
    textAlign: 'center', paddingTop: '40px', paddingBottom: '20px',
    borderTop: '1px solid rgba(153,179,214,0.1)',
  },
  segCtaText: {
    fontSize: '15px', lineHeight: 1.7, color: '#9ab0cc',
    maxWidth: '500px', margin: '0 auto 28px',
  },
  ctaBtn: {
    padding: '18px 48px', backgroundColor: '#4f8ef7', color: '#fff',
    border: 'none', borderRadius: '12px', fontSize: '17px', fontWeight: 800,
    cursor: 'pointer', letterSpacing: '0.02em',
    boxShadow: '0 8px 32px rgba(79,142,247,0.3)',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  ctaSub: {
    fontSize: '12px', color: '#4a6a88', margin: '16px 0 0',
  },
};
